type Fn<Input = any, Output = unknown> = (...args: Input[]) => Output;
type Piped<Input extends Fn> = Fn<NonNullable<ReturnType<Input>>, unknown>;
type ChainedReturn<FirstFn extends Fn, LastFn extends Fn> = Parameters<FirstFn> extends []
    ? () => ReturnType<LastFn> | undefined
    : (...args: Parameters<FirstFn>) => ReturnType<LastFn> | undefined;

export function chained<FnA extends Fn, FnB extends Piped<FnA>>(
    a: FnA,
    b: FnB
): ChainedReturn<FnA, FnB>;
export function chained<FnA extends Fn, FnB extends Piped<FnA>, FnC extends Piped<FnB>>(
    a: FnA,
    b: FnB,
    c: FnC
): ChainedReturn<FnA, FnC>;
export function chained<
    FnA extends Fn,
    FnB extends Piped<FnA>,
    FnC extends Piped<FnB>,
    FnD extends Piped<FnC>
>(a: FnA, b: FnB, c: FnC, d: FnD): ChainedReturn<FnA, FnD>;
export function chained<
    FnA extends Fn,
    FnB extends Piped<FnA>,
    FnC extends Piped<FnB>,
    FnD extends Piped<FnC>,
    FnE extends Piped<FnD>
>(a: FnA, b: FnB, c: FnC, d: FnD, e: FnE): ChainedReturn<FnA, FnE>;
export function chained<
    FnA extends Fn,
    FnB extends Piped<FnA>,
    FnC extends Piped<FnB>,
    FnD extends Piped<FnC>,
    FnE extends Piped<FnD>,
    FnF extends Piped<FnE>
>(a: FnA, b: FnB, c: FnC, d: FnD, e: FnE, f: FnF): ChainedReturn<FnA, FnF>;
export function chained(...funcs: Fn[]): unknown {
    return (...args: unknown[]) => {
        let result = funcs[0](...args);
        if (result === undefined || result === null) {
            return undefined;
        }
        for (let i = 1; i < funcs.length; i++) {
            result = funcs[i](result);
            if (result === undefined || result === null) {
                return undefined;
            }
        }

        return result;
    };
}
